import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, formatHM } from "@/lib/utils-tg";

export default function TimesheetPrintPage() {
  const { periodNumber } = useParams();
  const [searchParams] = useSearchParams();
  const subId = searchParams.get("sub");
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    const src = subId
      ? api.get(`/timesheet/submissions/${subId}/detail`)
      : api.get(`/timesheet/period/${periodNumber}`);
    src.then((r) => setData(r.data));
    api.get("/departments").then((r) => setDepartments(r.data));
    api.get("/workspace/public").then((r) => setCompanyName(r.data?.company_name || "")).catch(() => {});
  }, [periodNumber, subId]);

  useEffect(() => {
    if (data && departments.length) {
      // Auto-trigger print after a short delay
      setTimeout(() => window.print(), 600);
    }
  }, [data, departments]);

  if (!data) {
    return <div className="p-12 text-slate-400">Loading…</div>;
  }

  const deptMap = Object.fromEntries(departments.map((d) => [d.id, d.name]));
  const catMap = {};
  departments.forEach((d) => (d.categories || []).forEach((c) => (catMap[c.id] = c.name)));
  // Employee shown on the sheet: the submission's owner when a supervisor opens
  // it via ?sub=, otherwise the signed-in user.
  const emp = subId
    ? { name: data.employee_name || data.submission?.user_name, email: "", employee_id_number: null }
    : user;

  // group by date
  const byDate = {};
  data.entries.forEach((e) => {
    (byDate[e.date] = byDate[e.date] || []).push(e);
  });
  const dayTotals = Object.fromEntries(
    Object.entries(byDate).map(([d, es]) => [d, es.reduce((s, e) => s + (e.duration_minutes || 0), 0)])
  );

  // Compute week buckets (period spans 14 days: week 1 = days 0-6, week 2 = days 7-13).
  // We tag every date in the period with its week index so we can insert a
  // "Week N total" row right after each Saturday.
  const periodStart = new Date(`${data.period.start}T00:00:00`);
  const weekOfDate = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    const diffDays = Math.floor((d - periodStart) / 86400000);
    return diffDays < 7 ? 1 : 2;
  };
  const weekTotals = { 1: 0, 2: 0 };
  Object.entries(dayTotals).forEach(([iso, mins]) => {
    weekTotals[weekOfDate(iso)] += mins;
  });
  const sortedDates = Object.keys(byDate).sort();
  // Dates grouped by week, plus total entry-row count per week (for rowspans).
  const weekDates = { 1: [], 2: [] };
  sortedDates.forEach((iso) => weekDates[weekOfDate(iso)].push(iso));
  const weekRowCount = { 1: 0, 2: 0 };
  sortedDates.forEach((iso) => (weekRowCount[weekOfDate(iso)] += byDate[iso].length));

  return (
    <div className="bg-white text-slate-900 min-h-screen p-8 print:p-6 max-w-6xl mx-auto">
      <style>{`
        @media print {
          @page { size: letter landscape; margin: 0.4in; }
          body { background: white !important; }
          .no-print { display: none !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
        }
      `}</style>

      <div className="flex items-start justify-between mb-4 pb-4 border-b-2 border-slate-300">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Talk & Go" className="h-16 w-auto object-contain" />
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-bold">Timesheet</div>
          <div className="text-sm text-slate-600">Period {data.period.number} · {data.period.start} → {data.period.end}</div>
          <div className="text-xs text-slate-500 mt-0.5">Payday: {data.period.payday}</div>
        </div>
      </div>

      {companyName && (
        <div className="mb-4" data-testid="print-company-name">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Company</div>
          <div className="font-display text-xl font-bold text-slate-900">{companyName}</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Employee</div>
          <div className="font-display text-lg font-bold text-slate-900" data-testid="print-employee-name">{emp?.name}</div>
          <div className="text-slate-600">{emp?.email}</div>
          {emp?.employee_id_number && <div className="text-slate-600">ID: {emp.employee_id_number}</div>}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Totals</div>
          <div className="font-display text-lg font-bold" data-testid="print-total">{formatHM(data.total_minutes)}</div>
        </div>
      </div>

      <table className="w-full text-xs border-collapse table-fixed">
        <colgroup>
          <col style={{ width: "8%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "5%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "33%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
        </colgroup>
        <thead>
          <tr className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
            <th className="text-left px-2 py-2 border border-slate-300">Date</th>
            <th className="text-left px-2 py-2 border border-slate-300">Start</th>
            <th className="text-left px-2 py-2 border border-slate-300">End</th>
            <th className="text-left px-2 py-2 border border-slate-300">Hours</th>
            <th className="text-left px-2 py-2 border border-slate-300">Description</th>
            <th className="text-left px-2 py-2 border border-slate-300">Dept</th>
            <th className="text-left px-2 py-2 border border-slate-300">Category</th>
            <th className="text-center px-2 py-2 border border-slate-300">Total Day</th>
            <th className="text-center px-2 py-2 border border-slate-300">Total Week</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2].map((wk) => {
            const dates = weekDates[wk];
            if (!dates.length) return null;
            const firstIso = dates[0];
            return (
              <React.Fragment key={`wk-${wk}`}>
                {dates.map((d) => {
                  const entries = byDate[d];
                  return entries.map((e, i) => (
                    <tr key={e.id} className="border-b border-slate-200">
                      {i === 0 ? (
                        <td rowSpan={entries.length} className="px-2 py-1.5 border border-slate-300 whitespace-nowrap align-top font-medium">{d}</td>
                      ) : null}
                      <td className="px-2 py-1.5 border border-slate-300 whitespace-nowrap align-top">{e.start_time || ""}</td>
                      <td className="px-2 py-1.5 border border-slate-300 whitespace-nowrap align-top">{e.end_time || ""}</td>
                      <td className="px-2 py-1.5 border border-slate-300 whitespace-nowrap align-top">{formatHM(e.duration_minutes || 0)}</td>
                      <td className="px-2 py-1.5 border border-slate-300 align-top">{e.title || e.description}</td>
                      <td className="px-2 py-1.5 border border-slate-300 align-top">{deptMap[e.department_id] || ""}</td>
                      <td className="px-2 py-1.5 border border-slate-300 align-top">{catMap[e.category_id] || ""}</td>
                      {i === 0 ? (
                        <td rowSpan={entries.length} className="px-2 py-1.5 border border-slate-300 whitespace-nowrap align-top text-center font-semibold bg-slate-50">{formatHM(dayTotals[d])}</td>
                      ) : null}
                      {d === firstIso && i === 0 ? (
                        <td rowSpan={weekRowCount[wk]} className="px-2 py-1.5 border border-slate-300 whitespace-nowrap align-top text-center font-bold bg-slate-100">{formatHM(weekTotals[wk])}</td>
                      ) : null}
                    </tr>
                  ));
                })}
                <tr className="bg-slate-100 font-bold">
                  <td colSpan={8} className="px-2 py-1.5 border border-slate-300 text-right">Week {wk} Total</td>
                  <td className="px-2 py-1.5 border border-slate-300 text-center whitespace-nowrap">{formatHM(weekTotals[wk])}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr className="bg-emerald-50 font-bold">
            <td colSpan={7} className="px-2 py-2 border border-slate-300 text-right">2-Week Total</td>
            <td colSpan={2} className="px-2 py-2 border border-slate-300 whitespace-nowrap text-center">{formatHM(data.total_minutes)}</td>
          </tr>
        </tbody>
      </table>

      <div className="mt-12 grid grid-cols-2 gap-8 text-xs">
        <div>
          <div className="border-b border-slate-400 h-10 mb-1"></div>
          <div className="text-slate-600">Employee signature & date</div>
        </div>
        <div>
          <div className="border-b border-slate-400 h-10 mb-1"></div>
          <div className="text-slate-600">Supervisor signature & date</div>
        </div>
      </div>

      <div className="no-print mt-8 flex gap-2 justify-center">
        <button onClick={() => window.print()} className="tg-btn-primary">Print / Save as PDF</button>
        <button onClick={() => window.close()} className="tg-btn-secondary">Close</button>
      </div>
    </div>
  );
}
